import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import { Row, Col, Button, Skeleton, DatePicker, Form, Alert } from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import LoginChecked from "../Shared/LoginChecked";
import { NumberFormat } from "../Shared/Utility";
import { parseReportAmount } from "../../Pdf/PrintFormateNumber";
import { IdentificationIcon } from "@heroicons/react/24/outline";
import dayjs from "dayjs";
import moment from "moment";

const ASSET_ITEMS = [
  { label: "Inventory (at cost)", key: "stockValue" },
  { label: "Customer receivable", key: "customerReceivable" },
  { label: "Supplier receivable", key: "supplierReceivable" },
  { label: "Other receivables", key: "otherReceivable" },
  { label: "Cash in hand", key: "cashInHand" },
  { label: "Cash at bank", key: "cashAtBank" },
];

const LIABILITY_ITEMS = [
  { label: "Customer payable", key: "customerPayable" },
  { label: "Supplier payable", key: "supplierPayable" },
  { label: "Other payables", key: "otherPayable" },
];

function CapitalReport() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState({});
  const [asOfDate, setAsOfDate] = useState(dayjs());

  useEffect(() => {
    document.title = "Capital Report";
    setLoading(true);

    axios({
      method: "post",
      url: config.base_url + "Report/GetCapitalReport",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        Dated: dayjs(asOfDate).format("YYYY-MM-DD"),
      },
    })
      .then((response) => {
        if (response.data.status_code == 1) {
          setReportData(response.data);
        } else {
          setReportData({});
        }
        setLoading(false);
      })
      .catch(() => {
        setReportData({});
        setLoading(false);
      });
  }, [asOfDate, UserID, AccessKey]);

  const fmt = (val) => NumberFormat(parseReportAmount(val), 0);

  const totals = useMemo(() => {
    const totalAssets =
      parseReportAmount(reportData.totalAssets) ||
      ASSET_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(reportData[item.key]),
        0
      );
    const totalTradeLiabilities =
      parseReportAmount(reportData.totalTradeLiabilities) ||
      LIABILITY_ITEMS.reduce(
        (sum, item) => sum + parseReportAmount(reportData[item.key]),
        0
      );
    const netOwnersCapital =
      parseReportAmount(reportData.netOwnersCapital) ||
      totalAssets - totalTradeLiabilities;
    const capitalLedgerBalance = parseReportAmount(
      reportData.capitalLedgerBalance
    );
    const ledgerVariance =
      parseReportAmount(reportData.ledgerVariance) ||
      netOwnersCapital - capitalLedgerBalance;
    return {
      totalAssets,
      totalTradeLiabilities,
      netOwnersCapital,
      capitalLedgerBalance,
      ledgerVariance,
    };
  }, [reportData]);

  const asOfLabel = moment(asOfDate).format(config.date_format);
  const isPositive = totals.netOwnersCapital >= 0;

  const disabledDate = (current) =>
    current && current > moment().endOf("day");

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Capital Report</h3>

            <div className="header-actions">
              <NavLink
                to={`/report-capital/print-list?AsOfDate=${dayjs(asOfDate).format(
                  "YYYY-MM-DD"
                )}`}
                target="_blank"
              >
                <Button
                  type="dashed"
                  shape="round"
                  size="large"
                  icon={<PrinterOutlined />}
                >
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <div className="filter-wrap">
            <Form layout="inline">
              <Form.Item label="As of date">
                <DatePicker
                  value={asOfDate}
                  onChange={(date) => date && setAsOfDate(date)}
                  disabledDate={disabledDate}
                  allowClear={false}
                />
              </Form.Item>
            </Form>
          </div>

          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={`Owner's capital position as of ${asOfLabel}`}
            description="Net owner's capital = Total assets − Trade payables. Compare with the Capital ledger balance to verify books are in balance."
          />

          {loading ? (
            <Row>
              <Col xs={24} md={16}>
                <Skeleton active />
                <Skeleton active />
              </Col>
            </Row>
          ) : (
            <Row justify="center" gutter={[24, 24]}>
              <Col md={10} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <IdentificationIcon />
                      </span>
                      <h4>Total Assets</h4>
                    </div>
                  </div>
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      {ASSET_ITEMS.map((item) => (
                        <li key={item.key}>
                          <h4>{item.label}</h4>
                          <p>{fmt(reportData[item.key])}</p>
                        </li>
                      ))}
                      <li>
                        <h4 style={{ color: "#405289" }}>Total assets</h4>
                        <p className="netprofit">{fmt(totals.totalAssets)}</p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>

              <Col md={10} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-header">
                    <div className="fb-card-title">
                      <span>
                        <IdentificationIcon />
                      </span>
                      <h4>Trade Payables</h4>
                    </div>
                  </div>
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      {LIABILITY_ITEMS.map((item) => (
                        <li key={item.key}>
                          <h4>{item.label}</h4>
                          <p>{fmt(reportData[item.key])}</p>
                        </li>
                      ))}
                      <li>
                        <h4 style={{ color: "#405289" }}>
                          Total trade payables
                        </h4>
                        <p className="netprofit">
                          {fmt(totals.totalTradeLiabilities)}
                        </p>
                      </li>
                    </ul>
                  </div>
                </div>
              </Col>

              <Col md={20} sm={24}>
                <div className="fb-card">
                  <div className="fb-card-body table">
                    <ul className="profit-list">
                      <li>
                        <h4
                          style={{
                            color: isPositive ? "rgb(85 162 107)" : "#ec0d0d",
                          }}
                        >
                          Net owner&apos;s capital (assets − trade payables)
                        </h4>
                        <p className={isPositive ? "profit" : "loss"}>
                          {fmt(totals.netOwnersCapital)}
                        </p>
                      </li>
                      <li>
                        <h4>Capital per ledger (account type Capital)</h4>
                        <p>{fmt(totals.capitalLedgerBalance)}</p>
                      </li>
                      {Math.abs(totals.ledgerVariance) > 0.5 && (
                        <li>
                          <h4 style={{ color: "#b45309" }}>
                            Reconciliation variance
                          </h4>
                          <p>{fmt(totals.ledgerVariance)}</p>
                        </li>
                      )}
                    </ul>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </div>
      </div>
    </>
  );
}

export default CapitalReport;
